import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import Cookies from 'js-cookie';

import api from '../../../api/axios';
import { decryptData } from '../../../helpers/encryption';
import { IApiState } from '../../../types/api';
import { IUserSignIn, IUserToken } from '../../../types/user';
import { showAlert } from '../appToast/appToastSlice';
import { ISignInResponse } from './authTypes';
import { completeMfaAsync } from './completeMfaSlice';
import { persistAuthData } from './persistAuthData';
import { resendLoginOtpAsync } from './resendLoginOtpSlice';

interface ISignInCredentials {
  email: string;
  passcode: string;
}

export interface ISignInRejectPayload extends ISignInResponse {
  unauthorized?: boolean;
}

const tokenCookie = Cookies.get('tokenData');
const parsedTokenCookie = tokenCookie
  ? (JSON.parse(tokenCookie) as IUserToken)
  : undefined;

const otherAuthData = localStorage.getItem('otherAuthData');
const parsedOtherAuthData = otherAuthData
  ? (decryptData(otherAuthData) as { user?: IUserSignIn })
  : undefined;

const authData =
  parsedTokenCookie && parsedOtherAuthData
    ? ({
        status: true,
        message: '',
        data: { token: { ...parsedTokenCookie }, ...parsedOtherAuthData },
        errors: [],
      } as ISignInResponse)
    : null;

const initialState: IApiState<ISignInResponse> = {
  value: authData,
  status: 'idle',
  error: '',
};

export const signInAsync = createAsyncThunk<
  ISignInResponse,
  ISignInCredentials,
  { rejectValue: ISignInRejectPayload }
>('auth/signIn', async ({ email, passcode }, { dispatch, rejectWithValue }) => {
  try {
    const normalisedEmail = email.toLowerCase();

    const { data } = await api.post<ISignInResponse>('/auth/login', {
      email: normalisedEmail,
      passcode,
    });

    if (data?.data?.requiresMfaToken) {
      return {
        ...data,
        data: { ...data.data, email: normalisedEmail },
      };
    }

    persistAuthData({
      user: data?.data?.user,
      token: data?.data?.token,
      email,
      dispatch,
    });

    return data;
  } catch (err) {
    const error = err as AxiosError<ISignInResponse>;

    dispatch(
      showAlert({
        status: 'error',
        description:
          error?.response?.data?.message ??
          'An error occurred while signing you in, try again',
      }),
    );

    return rejectWithValue({
      ...error.response?.data,
    });
  }
});

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    resetSignIn: (state) => {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
    updateAuthToken: (state, action: PayloadAction<IUserToken | undefined>) => {
      state.value = {
        ...state.value,
        data: { ...state.value?.data, token: action?.payload },
      };
    },
    updatePin: (state, action: PayloadAction<boolean | undefined>) => {
      state.value = {
        ...state.value,
        data: { ...state.value?.data, user: { hasPin: action.payload } },
      };
    },
    clearMfaChallenge: (state) => {
      if (state.value?.data?.requiresMfaToken) {
        state.value = null;
        state.status = 'idle';
        state.error = '';
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signInAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(signInAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload;
      })
      .addCase(signInAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
        // Don't clobber an in-flight MFA challenge on a failed retry.
        if (!state.value?.data?.requiresMfaToken) {
          state.value = action.payload as ISignInResponse;
        }
      })
      .addCase(resendLoginOtpAsync.fulfilled, (state, action) => {
        const newSessionToken = action.payload?.data?.sessionToken;

        if (state.value?.data?.requiresMfaToken && newSessionToken) {
          state.value = {
            ...state.value,
            data: { ...state.value.data, sessionToken: newSessionToken },
          };
        }
      })
      .addCase(completeMfaAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload;
      });
  },
});

export const { resetSignIn, updateAuthToken, updatePin, clearMfaChallenge } =
  authSlice.actions;

export default authSlice.reducer;
